// src/games/darkforest/scenes/river.js
import { DIFFICULTY_CLASS } from '../../../game-system/skills/skillSystem';

export const river = {
  stream_approach: {
    id: 'stream_approach',
    title: "Approaching the Stream",
    description: `Following the left-hand path from the fork, you descend slightly through increasingly lush vegetation. The sound of running water grows louder, and the air becomes noticeably more humid and fresh.

Ferns and flowering plants crowd the edges of the path, which narrows as it approaches the water source. Sunlight filters through the canopy in greater abundance here, creating dappled patterns on the forest floor.

After a short walk, you reach the bank of a clear, fast-flowing stream about fifteen feet wide. The water tumbles over smooth stones, creating a pleasant musical sound. On the far bank, the path appears to continue into the forest.`,
    choices: [
      {
        id: 'cross_stream',
        text: "Look for a way to cross the stream",
        nextScene: 'stream_crossing_options',
        requires: null
      },
      {
        id: 'follow_upstream',
        text: "Follow the stream upstream",
        nextScene: 'upstream_path',
        requires: null
      },
      {
        id: 'follow_downstream',
        text: "Follow the stream downstream",
        nextScene: 'downstream_path',
        requires: null
      },
      {
        id: 'rest_by_stream',
        text: "Rest by the stream for a while",
        nextScene: 'stream_rest',
        requires: null
      }
    ]
  },
  
  stream_crossing_options: {
    id: 'stream_crossing_options',
    title: "Stream Crossing",
    description: `You survey the stream, looking for the best way to cross to the other side. The water is clear and not particularly deep - perhaps knee-height in the middle - but the current is swift, and the stones on the bottom look slippery.

About thirty feet upstream, several large, flat rocks protrude from the water, potentially forming a path across if you could balance on them. They're spaced widely enough to require some careful jumping.

Alternatively, you could try wading directly across at this point, or look for a fallen log or other natural bridge nearby.`,
    choices: [
      {
        id: 'use_stepping_stones',
        text: "Use the stepping stones upstream",
        nextScene: 'stepping_stones',
        requires: null
      },
      {
        id: 'wade_across',
        text: "Wade directly across the stream",
        nextScene: 'wade_stream',
        requires: null
      },
      {
        id: 'search_for_bridge',
        text: "Search for a natural bridge nearby",
        nextScene: 'find_log_bridge',
        requires: null
      }
    ],
    encounters: ['stream_crossing']
  },
  
  stepping_stones: {
    id: 'stepping_stones',
    title: "Stepping Stones",
    description: `You make your way upstream to where the large, flat stones break the surface of the water. From the bank, they look like they could indeed form a path across - if you're agile enough to jump from one to another.

The first stone is close to the bank, easily accessible. But the gaps between subsequent stones would require careful balance and moderately athletic jumps. The stones themselves appear stable, but their surfaces are slick with spray and moss.`,
    skillChecks: [
      {
        skill: 'acrobatics',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Cross using the stepping stones",
        results: {
          success: "With careful balance and precise jumps, you make your way across the stream from stone to stone. A couple of times, your foot nearly slips on the wet surface, but you maintain your balance and reach the far bank dry and safe.",
          rewards: {
            experience: 25
          },
          failure: "You make it across several stones successfully, but midway across, your foot slips on a particularly mossy stone. You lose your balance and fall into the stream with a splash. The cold water is a shock, and the current pulls at you, but you manage to stand and wade the rest of the way across, thoroughly soaked but otherwise unharmed."
        }
      }
    ],
    choices: [
      {
        id: 'wade_across',
        text: "Decide to wade across instead",
        nextScene: 'wade_stream',
        requires: null
      },
      {
        id: 'search_for_bridge',
        text: "Look for an easier crossing point",
        nextScene: 'find_log_bridge',
        requires: null
      }
    ]
  },
  
  wade_stream: {
    id: 'wade_stream',
    title: "Wading Across",
    description: `You decide the simplest approach is to wade directly across the stream. Securing your belongings to keep them dry, you step into the water.

The stream is cold and refreshing, the current noticeable but not overwhelming. The rocky bottom is indeed slippery in places, requiring careful footing. At its deepest point, the water reaches just above your knees.`,
    skillChecks: [
      {
        skill: 'athletics',
        difficultyClass: DIFFICULTY_CLASS.EASY,
        description: "Wade safely across the stream",
        results: {
          success: "You make your way steadily across the stream, planting your feet carefully on the rocky bottom. The current pushes against your legs, but you maintain your balance and reach the far bank without incident, your feet and legs wet but the rest of you dry.",
          rewards: {
            experience: 15
          },
          failure: "About halfway across, your foot slips on a particularly smooth stone. You stumble forward, catching yourself before falling completely, but not before dunking one arm and part of your torso into the cold water. You recover and make it to the far bank, somewhat wetter than you'd hoped to be."
        }
      }
    ],
    choices: [
      {
        id: 'continue_path',
        text: "Continue along the path on the far bank",
        nextScene: 'far_bank_path',
        requires: null
      },
      {
        id: 'search_surroundings',
        text: "Look around the area before continuing",
        nextScene: 'far_bank_search',
        requires: null
      }
    ]
  },
  
  find_log_bridge: {
    id: 'find_log_bridge',
    title: "Natural Bridge",
    description: `You search along the bank for an easier way to cross the stream. After a short distance downstream, you find exactly what you were hoping for - a large fallen tree forms a natural bridge across the water.

The tree appears to have fallen relatively recently, as many of its smaller branches are still intact, and patches of bark still cling to its surface. Its trunk is thick and seems stable, though somewhat rounded on top.`,
    skillChecks: [
      {
        skill: 'nature',
        difficultyClass: DIFFICULTY_CLASS.EASY,
        description: "Check if the log is stable and safe",
        results: {
          success: "You examine the log carefully and determine it's a sturdy oak that appears to be well-anchored on both banks. While the surface isn't perfectly flat, it should provide a safe crossing if you're reasonably careful.",
          rewards: {
            experience: 10
          },
          failure: "The log looks stable enough, but it's hard to be certain how securely it's positioned without testing it."
        }
      }
    ],
    choices: [
      {
        id: 'cross_log',
        text: "Cross using the fallen tree",
        nextScene: 'log_crossing',
        requires: null
      },
      {
        id: 'wade_instead',
        text: "Decide to wade across instead",
        nextScene: 'wade_stream',
        requires: null
      }
    ]
  },
  
  log_crossing: {
    id: 'log_crossing',
    title: "Log Crossing",
    description: `You carefully mount the fallen tree and begin to make your way across. The bark provides some grip, but the rounded surface requires attention to balance.

You move slowly, testing each step before committing your weight. Below, the stream rushes past, its surface broken by occasional small rapids around stones.`,
    skillChecks: [
      {
        skill: 'acrobatics',
        difficultyClass: DIFFICULTY_CLASS.EASY,
        description: "Cross the log bridge with balance",
        results: {
          success: "With careful attention to your balance, you make your way across the log without incident. The crossing is easier than it looked from the bank, and you soon step onto solid ground on the far side, completely dry.",
          rewards: {
            experience: 15
          },
          failure: "About halfway across, your foot slips on a patch of moss. You wobble precariously for a moment, arms out to your sides. You manage to avoid falling into the stream, but in the process, you end up straddling the log awkwardly and have to scoot the rest of the way across in an undignified but effective manner."
        }
      }
    ],
    choices: [
      {
        id: 'continue_path',
        text: "Continue along the path on the far bank",
        nextScene: 'far_bank_path',
        requires: null
      },
      {
        id: 'search_surroundings',
        text: "Look around the area before continuing",
        nextScene: 'far_bank_search',
        requires: null
      }
    ]
  },
  
  stream_rest: {
    id: 'stream_rest',
    title: "Resting by the Stream",
    description: `You decide to take a short rest by the stream, finding a comfortable spot on a smooth boulder near the water's edge. The constant sound of the flowing water is soothing, and the cool mist refreshing after your journey through the forest.

You take the opportunity to refill your waterskin with the clear stream water and wash away some of the dust and sweat of travel. A few colorful birds flit among the trees on the opposite bank, and small fish dart among the stones in the shallows.`,
    choices: [
      {
        id: 'meditate',
        text: "Meditate by the stream",
        nextScene: 'stream_meditation',
        requires: null
      },
      {
        id: 'search_stream',
        text: "Search along the stream bank",
        nextScene: 'stream_bank_search',
        requires: null
      },
      {
        id: 'cross_stream',
        text: "Look for a way to cross the stream",
        nextScene: 'stream_crossing_options',
        requires: null
      },
      {
        id: 'follow_upstream',
        text: "Follow the stream upstream",
        nextScene: 'upstream_path',
        requires: null
      },
      {
        id: 'follow_downstream',
        text: "Follow the stream downstream",
        nextScene: 'downstream_path',
        requires: null
      }
    ],
    rewards: {
      rest: {
        health: "minor",
        fatigue: "remove"
      }
    }
  },
  
  stream_meditation: {
    id: 'stream_meditation',
    title: "Stream Meditation",
    description: `Finding a comfortable position on a smooth rock by the water's edge, you close your eyes and focus on the sounds of the forest and the stream. The rushing water creates a natural rhythm that helps quiet your mind.

As your breathing slows and deepens, you become more aware of the subtle sounds around you - the different bird calls, the rustle of leaves in the gentle breeze, the occasional splash of a fish jumping. The scents of the forest seem sharper too - damp earth, flowers, and the clean mineral smell of the water.

Time passes in a peaceful blur as you allow yourself to simply exist in the moment.`,
    skillChecks: [
      {
        skill: 'insight',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Achieve a deeper meditative state",
        results: {
          success: "As your meditation deepens, you experience a profound sense of connection with the forest around you. Insights about your journey seem to bubble up from your subconscious. You realize that the path downstream will likely lead to the ancient ruins you seek, while upstream may lead to higher ground with a better vantage point.",
          rewards: {
            experience: 25,
            storyFlags: ['forest_insight'],
            temporaryBonus: {
              skill: 'perception',
              duration: 'medium',
              value: 2
            }
          },
          failure: "While pleasant and refreshing, your meditation remains relatively shallow. Your mind keeps returning to thoughts of your journey and what lies ahead, making it difficult to achieve a deeper state."
        }
      }
    ],
    choices: [
      {
        id: 'cross_stream',
        text: "Look for a way to cross the stream",
        nextScene: 'stream_crossing_options',
        requires: null
      },
      {
        id: 'follow_upstream',
        text: "Follow the stream upstream",
        nextScene: 'upstream_path',
        requires: null
      },
      {
        id: 'follow_downstream',
        text: "Follow the stream downstream",
        nextScene: 'downstream_path',
        requires: null
      }
    ]
  },
  
  stream_bank_search: {
    id: 'stream_bank_search',
    title: "Searching the Stream Bank",
    description: `You search along the stream bank, looking for anything interesting or useful. The damp soil near the water is soft and shows various animal tracks - deer, rabbits, and what might be a fox or small wolf.

The vegetation here is lush and varied, with several types of ferns, flowering plants, and mosses covering the ground and rocks. Some of the plants look like they might have medicinal properties.`,
    skillChecks: [
      {
        skill: 'survival',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Identify useful plants or resources",
        results: {
          success: "You recognize several useful plants growing near the stream. There's a patch of moonwort, good for treating wounds, and some sweetroot that can be chewed to restore energy. You gather a few of each, carefully wrapping them in a leaf to preserve them.",
          rewards: {
            items: ['minor_healing_potion'],
            experience: 25
          },
          failure: "While many of the plants look potentially useful, you can't confidently identify which are safe to use and which might be harmful. Better not to risk it."
        }
      },
      {
        skill: 'investigation',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Search thoroughly for anything unusual",
        results: {
          success: "Near a twisted tree root at the water's edge, you spot something glinting. Investigating further, you find a small metal object partially buried in the mud - an old silver ring engraved with leaf patterns. It doesn't appear valuable, but it's an interesting find.",
          rewards: {
            items: ['silver_ring'],
            experience: 30
          },
          failure: "Your search turns up nothing beyond the usual forest detritus - fallen leaves, twigs, and stones worn smooth by the water."
        }
      }
    ],
    choices: [
      {
        id: 'cross_stream',
        text: "Look for a way to cross the stream",
        nextScene: 'stream_crossing_options',
        requires: null
      },
      {
        id: 'follow_upstream',
        text: "Follow the stream upstream",
        nextScene: 'upstream_path',
        requires: null
      },
      {
        id: 'follow_downstream',
        text: "Follow the stream downstream",
        nextScene: 'downstream_path',
        requires: null
      }
    ]
  },
  
  upstream_path: {
    id: 'upstream_path',
    title: "Upstream Path",
    description: `You decide to follow the stream upstream, moving along its bank where the vegetation allows. The path becomes less distinct here, requiring you to occasionally push through underbrush or detour around particularly dense patches of vegetation.

The ground begins to slope upward more noticeably, and the stream becomes narrower and faster. The sound of rushing water grows louder, suggesting there might be a waterfall or rapids ahead.

As you climb, you get occasional glimpses through the trees of higher ground and what appears to be a ridge line in the distance.`,
    choices: [
      {
        id: 'continue_upstream',
        text: "Continue following the stream upward",
        nextScene: 'waterfall_approach',
        requires: null
      },
      {
        id: 'climb_overlook',
        text: "Climb toward the ridge for a better view",
        nextScene: 'forest_overlook',
        requires: null
      },
      {
        id: 'return_to_crossing',
        text: "Return to the stream crossing",
        nextScene: 'stream_approach',
        requires: null
      }
    ]
  },
  
  waterfall_approach: {
    id: 'waterfall_approach',
    title: "Approaching the Waterfall",
    description: `As you continue upstream, the path grows steeper and more challenging. The stream rushes over increasingly large rocks, forming small cascades and pools. The roar of falling water grows louder until you finally emerge into a clearing dominated by a beautiful waterfall.

Water tumbles about thirty feet down a rocky cliff face into a deep, clear pool below. Rainbow-like mists rise where the water impacts, and the surrounding rocks are covered with emerald moss and delicate ferns. The scene is breathtakingly peaceful yet powerful.

A narrow path appears to wind up the side of the cliff, allowing access to the top of the waterfall.`,
    choices: [
      {
        id: 'climb_waterfall',
        text: "Climb the path to the top of the waterfall",
        nextScene: 'waterfall_top',
        requires: null
      },
      {
        id: 'examine_pool',
        text: "Examine the pool at the base of the waterfall",
        nextScene: 'waterfall_pool',
        requires: null
      },
      {
        id: 'return_downstream',
        text: "Return downstream",
        nextScene: 'upstream_path',
        requires: null
      }
    ]
  },
  
  waterfall_pool: {
    id: 'waterfall_pool',
    title: "Waterfall Pool",
    description: `You approach the clear pool at the base of the waterfall. The water is remarkably clear and appears quite deep at the center, where the falling water has carved out a basin in the rock over countless years.

The constant churning of the waterfall creates a slight current in the pool, but near the edges, the water is calm and inviting. Small fish dart in the shallows, and flat stones create comfortable places to sit at the water's edge.

Behind the falling sheet of water, you think you might see a dark space - possibly a small cave or hollow in the cliff face.`,
    choices: [
      {
        id: 'swim_pool',
        text: "Swim in the pool",
        nextScene: 'swim_waterfall_pool',
        requires: null
      },
      {
        id: 'investigate_cave',
        text: "Check behind the waterfall",
        nextScene: 'waterfall_cave',
        requires: null
      },
      {
        id: 'climb_waterfall',
        text: "Climb to the top of the waterfall",
        nextScene: 'waterfall_top',
        requires: null
      },
      {
        id: 'return_downstream',
        text: "Return downstream",
        nextScene: 'upstream_path',
        requires: null
      }
    ]
  },
  
  swim_waterfall_pool: {
    id: 'swim_waterfall_pool',
    title: "Swimming in the Pool",
    description: `You find a suitable spot to set aside your gear and remove any heavy or metal items that would weigh you down. Then you wade into the pool, finding the water bracingly cool but not uncomfortably cold.

As you swim toward the center, you can feel the current created by the waterfall, though it's not strong enough to be dangerous. The water is incredibly refreshing after your journey through the forest, washing away dust and fatigue.

The sunlight filtering through the trees creates dancing patterns on the underwater rocks, and the roar of the waterfall seems to block out all other sounds, creating a peaceful isolation.`,
    choices: [
      {
        id: 'dive_deeper',
        text: "Dive to explore the deeper water",
        nextScene: 'deep_pool_dive',
        requires: null
      },
      {
        id: 'investigate_cave',
        text: "Swim behind the waterfall",
        nextScene: 'waterfall_cave',
        requires: null
      },
      {
        id: 'return_to_shore',
        text: "Return to shore and continue your journey",
        nextScene: 'waterfall_approach',
        requires: null
      }
    ],
    rewards: {
      rest: {
        health: "minor",
        fatigue: "remove"
      }
    }
  },
  
  deep_pool_dive: {
    id: 'deep_pool_dive',
    title: "Deep Pool Dive",
    description: `Taking a deep breath, you dive beneath the surface of the pool, opening your eyes underwater to explore the depths. The water is remarkably clear, allowing you to see the rocky bottom and underwater landscape in detail.

The pool is deeper than it first appeared - perhaps fifteen feet at its deepest point. The bottom is covered with smooth, water-worn stones in various colors, and small fish dart away at your approach.`,
    skillChecks: [
      {
        skill: 'perception',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Search for anything unusual underwater",
        results: {
          success: "In the deepest part of the pool, something catches your eye - a metallic glint among the rocks. Swimming down, you discover a small, ornate dagger wedged between stones. It appears to be made of silver or a similar metal, with a hilt decorated with aquatic motifs. Despite being underwater for who knows how long, it shows remarkably little corrosion.",
          rewards: {
            items: ['silver_dagger'],
            experience: 30
          },
          failure: "While beautiful, the underwater environment reveals nothing particularly unusual or valuable - just rocks, some aquatic plants, and small fish."
        }
      }
    ],
    choices: [
      {
        id: 'investigate_cave',
        text: "Swim behind the waterfall",
        nextScene: 'waterfall_cave',
        requires: null
      },
      {
        id: 'return_to_shore',
        text: "Return to shore",
        nextScene: 'waterfall_approach',
        requires: null
      }
    ]
  },
  
  waterfall_cave: {
    id: 'waterfall_cave',
    title: "Behind the Waterfall",
    description: `You swim toward the falling sheet of water, fighting against the increasing current as you get closer. Timing your move carefully, you push through the curtain of falling water, bracing yourself against the heavy impact.

On the other side, you find yourself in a small cave carved into the cliff face by centuries of water erosion. It's large enough to stand comfortably, with a rocky floor that rises above the water level. The roar of the waterfall is somewhat muffled in here, creating an unusual acoustic space.

The cave walls glisten with moisture, and in the dim light filtering through the waterfall, you can make out what appear to be markings or carvings on the back wall.`,
    skillChecks: [
      {
        skill: 'investigation',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Examine the cave thoroughly",
        results: {
          success: "Looking closely at the back wall, you realize the markings are deliberately carved symbols similar to those you've seen elsewhere in the forest. They appear to depict a map of the area, showing the stream, the forest, and what might be the ancient ruins. One location is marked with a distinctive spiral symbol - perhaps indicating something important.",
          rewards: {
            experience: 35,
            storyFlags: ['waterfall_map']
          },
          failure: "The markings on the wall appear to be some kind of symbols or primitive art, but their meaning isn't clear to you. They could be random carvings or have some significance you can't decipher."
        }
      }
    ],
    choices: [
      {
        id: 'leave_cave',
        text: "Leave the cave and return to the pool",
        nextScene: 'waterfall_approach',
        requires: null
      },
      {
        id: 'search_cave_floor',
        text: "Search the cave floor",
        nextScene: 'cave_floor_search',
        requires: null
      }
    ]
  },
  
  cave_floor_search: {
    id: 'cave_floor_search',
    title: "Searching the Cave Floor",
    description: `You crouch down to examine the cave floor more carefully. It's primarily smooth stone, worn by water over countless years, with occasional depressions where small pools have formed from the waterfall spray.

In the dim light, it's challenging to see details, but you run your hands carefully over the surface, feeling for anything unusual.`,
    skillChecks: [
      {
        skill: 'perception',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Search the cave floor carefully",
        results: {
          success: "Your fingers brush against something smooth and regular in shape - definitely not a natural stone. Picking it up, you find a small, polished blue stone pendant on a tarnished silver chain. The pendant is carved with symbols matching those on the wall, and it seems to glow faintly with an inner light when you hold it up to the waterfall.",
          rewards: {
            items: ['water_pendant'],
            experience: 35
          },
          failure: "Despite a thorough search, you find nothing but smooth stones, a few small water-worn twigs, and the occasional fish bone - perhaps from an otter's meal."
        }
      }
    ],
    choices: [
      {
        id: 'leave_cave',
        text: "Leave the cave and return to the pool",
        nextScene: 'waterfall_approach',
        requires: null
      }
    ]
  },
  
  waterfall_top: {
    id: 'waterfall_top',
    title: "Top of the Waterfall",
    description: `You climb the narrow, winding path up the side of the cliff. It's steep in places but manageable, with natural rock formations providing handholds where needed.

Reaching the top, you find yourself on a rocky outcrop overlooking the pool below. The stream continues behind you, flowing from deeper in the forest before cascading over the edge. From this vantage point, you have a much better view of the surrounding forest, with sightlines that weren't possible from below.

The air up here feels fresher, and a light breeze carries the scent of pine and distant flowering plants.`,
    skillChecks: [
      {
        skill: 'perception',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Survey the surrounding landscape",
        results: {
          success: "From this height, you can make out several landmark features of the forest that were hidden from below. In the distance to the east, you spot what appears to be the ruins of stone structures - likely the ancient temple you're seeking. You also notice a thin column of smoke rising from the forest to the north, suggesting someone has a campfire.",
          rewards: {
            experience: 25,
            storyFlags: ['spotted_ruins', 'spotted_camp']
          },
          failure: "While the view is certainly impressive, the dense forest canopy obscures most specific landmarks. You can see the general lay of the land, but can't identify any particular points of interest."
        }
      }
    ],
    choices: [
      {
        id: 'follow_upper_stream',
        text: "Follow the stream further upstream",
        nextScene: 'upper_stream_path',
        requires: null
      },
      {
        id: 'descend_waterfall',
        text: "Climb back down to the pool",
        nextScene: 'waterfall_approach',
        requires: null
      },
      {
        id: 'head_toward_ruins',
        text: "Try to head toward the ruins",
        nextScene: 'forest_ridge',
        requires: 'spotted_ruins'
      },
      {
        id: 'investigate_smoke',
        text: "Investigate the smoke source",
        nextScene: 'approach_camp',
        requires: 'spotted_camp'
      }
    ]
  },
  
  downstream_path: {
    id: 'downstream_path',
    title: "Downstream Path",
    description: `You follow the stream as it flows downstream, keeping to its bank where possible. The path here is easier than upstream, with the ground generally sloping downward and the vegetation less dense.

The stream widens and slows as you continue, becoming more meandering. In places, small sandy beaches form on the insides of bends, and fallen logs create natural dams that form deeper pools.

The forest on either side of the stream appears ancient and largely undisturbed. Massive trees rise on both banks, their roots sometimes visible where the water has eroded the soil away.`,
    choices: [
      {
        id: 'continue_downstream',
        text: "Continue following downstream",
        nextScene: 'stream_widens',
        requires: null
      },
      {
        id: 'examine_pools',
        text: "Investigate one of the deeper pools",
        nextScene: 'deep_forest_pool',
        requires: null
      },
      {
        id: 'cross_stream_here',
        text: "Cross to the other bank",
        nextScene: 'stream_crossing_options',
        requires: null
      },
      {
        id: 'return_upstream',
        text: "Return upstream",
        nextScene: 'stream_approach',
        requires: null
      }
    ]
  }
};